import { APISurveyAttributes, ArdoqId, PrivilegeLabel } from '@ardoq/api-types';
import { FeatureContext, Features, featureOperations } from '@ardoq/features';
import { hasPrivilege } from '@ardoq/privileges';
import {
  Permissions,
  hasResourcePermission,
} from 'streams/currentUserPermissions/permissionInterface';
import { permissionsOperations } from '@ardoq/access-control';
import { getCurrentUserPermissionContext } from 'streams/currentUserPermissions/currentUserPermissionContext$';

/**
 * Check if the current user has the correct access to
 * create a new survey (checks for privileges and feature).
 * @returns {boolean}
 */
const canCreateSurvey = (featureContext: FeatureContext): boolean =>
  featureOperations.hasFeature(featureContext, Features.SURVEYS) &&
  permissionsOperations.hasWriterRolePlus(getCurrentUserPermissionContext()) &&
  hasPrivilege(PrivilegeLabel.CREATE_SURVEY);

/**
 * Check if the current user has the correct access to
 * Admin a survey (checks for permissions and feature).
 * @returns {boolean}
 */
const canAdminSurvey = (
  featureContext: FeatureContext,
  surveyId?: ArdoqId
): boolean =>
  featureOperations.hasFeature(featureContext, Features.SURVEYS) &&
  permissionsOperations.hasWriterRolePlus(getCurrentUserPermissionContext()) &&
  (hasPrivilege(PrivilegeLabel.GLOBAL_DATA_ACCESS) ||
    hasResourcePermission(
      Permissions.SURVEY_ADMIN,
      getCurrentUserPermissionContext(),
      surveyId
    ));

/**
 * Check if the current user has the correct access to
 * Admin a survey (checks for permissions and feature).
 * @returns {boolean}
 */
const canEditSurvey = (
  featureContext: FeatureContext,
  surveyId?: ArdoqId
): boolean =>
  featureOperations.hasFeature(featureContext, Features.SURVEYS) &&
  permissionsOperations.hasWriterRolePlus(getCurrentUserPermissionContext()) &&
  (hasPrivilege(PrivilegeLabel.GLOBAL_DATA_ACCESS) ||
    hasResourcePermission(
      Permissions.SURVEY_EDIT,
      getCurrentUserPermissionContext(),
      surveyId
    ) ||
    canAdminSurvey(featureContext, surveyId));

/**
 * Check if the current user has the correct access to
 * read a new survey (checks for permissions and feature).
 * @returns {boolean}
 */
const canAccessSurvey = (
  featureContext: FeatureContext,
  survey: APISurveyAttributes | undefined
): boolean => {
  if (!survey) {
    return false;
  }
  return (
    featureOperations.hasFeature(featureContext, Features.SURVEYS) &&
    (hasPrivilege(PrivilegeLabel.GLOBAL_DATA_ACCESS) ||
      hasResourcePermission(
        Permissions.SURVEY_ACCESS,
        getCurrentUserPermissionContext(),
        survey._id
      ))
  );
};

/**
 * Check if the current user has the correct access to
 * answer a survey (checks for permissions and feature and publish status).
 * @returns {boolean}
 */
const canSubmitSurvey = (
  featureContext: FeatureContext,
  survey: APISurveyAttributes | undefined
): boolean => {
  if (!survey) {
    return false;
  }
  return canAccessSurvey(featureContext, survey) && survey.published;
};

/**
 * Check if the current user has the correct access to
 * copy the survey as is (checks for permissions and feature).
 * @returns {boolean}
 */
const canCopySurvey = (
  featureContext: FeatureContext,
  surveyId: ArdoqId
): boolean => {
  return (
    canCreateSurvey(featureContext) && canEditSurvey(featureContext, surveyId)
  );
};

export const surveyAccessControlInterface = {
  canCreateSurvey,
  canAdminSurvey,
  canEditSurvey,
  canAccessSurvey,
  canSubmitSurvey,
  canCopySurvey,
};
